/**
 * Registry of AI tools available in the email drawer's AI panel.
 *
 * Each tool definition is a plain JS object — to add a new tool, drop another
 * entry into the `AI_TOOLS` map. The panel UI reads each tool's `default_*`
 * fields to seed editable inputs; user edits are persisted to localStorage
 * keyed by `localStorageKey(tool.id, field)` so devs don't lose tweaks on
 * refresh.
 *
 * Prompts use `{{placeholders}}` that the backend substitutes server-side
 * before calling OpenAI. See `PLACEHOLDER_VARIABLES` for the full list of
 * automatic variables; tools can also define their own custom variables
 * via the `custom_inputs` array.
 */

export const AI_MODELS = [
	"gpt-4o-mini",
	"gpt-4o",
	"gpt-4.1-mini",
	"gpt-4.1",
	"gpt-5-mini",
	"gpt-5",
	"o1-mini",
	"o3-mini",
]

/**
 * Built-in placeholder variables auto-substituted by the backend in both
 * system_instructions and user_instructions.
 */
export const PLACEHOLDER_VARIABLES = [
	{ key: "body", description: "Full email body — HTML if available, plain text otherwise" },
	{ key: "body_text", description: "Plain-text version of the body (HTML stripped + whitespace collapsed)" },
	{ key: "body_length_chars", description: "Character count of the plain-text body" },
	{ key: "subject", description: "Email subject line" },
	{ key: "from", description: 'Formatted from — "Name <address>" or just address' },
	{ key: "from_name", description: "Sender's display name only" },
	{ key: "from_address", description: "Sender's email address only" },
	{ key: "from_domain", description: "Sender's domain (everything after @, lowercased)" },
	{ key: "to", description: "Comma-separated to recipients (formatted)" },
	{ key: "cc", description: "Comma-separated cc recipients (formatted)" },
	{ key: "occurred_at", description: "Received timestamp as ISO-8601 string" },
	{ key: "internet_message_id", description: "RFC 5322 Message-ID header" },
	{ key: "link_count", description: "Number of <a href> tags in the HTML body" },
	{ key: "image_count", description: "Number of <img> tags in the HTML body" },
	{ key: "has_unsubscribe", description: '"yes" or "no" — whether an unsubscribe / opt-out link was detected' },
]

/**
 * Default classifications used by the email classifier tool. Stitched into
 * the user prompt as `{{categories}}` and rendered as a fixed badge list in
 * the panel (the `categories` input is `editable: false`).
 *
 * Naming convention: two-level lowercase keys, dot-separated, with underscores
 * for compound second-level names — `booking.amendment_request`,
 * `payment_intent.succeeded`-style. Top level is the domain; second level is
 * the specific event or content type.
 *
 * Booking lifecycle uses a parallel path:
 *   booking.new → booking.confirmed                   (the happy path)
 *   booking.amendment_request → booking.amendment_confirmed   (changes mid-flight)
 *   booking.supplier_confirmation                     (supplier-side acks)
 */
const DEFAULT_CLASSIFICATIONS = [
	"enquiry.new",
	"business.partner",
	"booking.new",
	"booking.confirmed",
	"booking.amendment_request",
	"booking.amendment_confirmed",
	"booking.support",
	"booking.flight_details",
	"booking.supplier_confirmation",
	"supplier.outreach",
	"supplier.rates",
	"supplier.stop_sales",
	"supplier.support_ticket",
	"marketing.newsletter",
	"marketing.cold",
	"personal.sensitive",
	"financial.invoice",
	"financial.statement",
	"financial.sensitive",
	"internal.notification",
	"automated.auto_reply",
	"automated.notification",
]

/**
 * Schema for the classifier's structured output. Constrains the model to
 * always return these fields so we can render the result reliably.
 *
 * The `references` object is populated by scanning the entire email content
 * (top message + quoted history) for booking/quote/enquiry references — it's
 * not just a classification side-effect, it's the primary entry point for
 * future "link this email to the booking record" automation.
 */
const CLASSIFIER_SCHEMA = {
	type: "object",
	additionalProperties: false,
	properties: {
		matched_classification: {
			type: ["string", "null"],
			description: "The chosen classification, or null if none of the provided options fit.",
		},
		confidence: {
			type: "number",
			minimum: 0,
			maximum: 1,
			description: "Confidence in the classification, between 0 and 1.",
		},
		suggested_alternative: {
			type: ["string", "null"],
			description:
				"If matched_classification is null, suggest a new classification name that would fit. Otherwise null.",
		},
		reasoning: {
			type: "string",
			description: "1-2 sentence explanation grounded in the email content.",
		},
		visual_evidence_reasoning: {
			type: "string",
			description:
				"Explain whether attached/inline images or files were used as classification evidence. If no visual/file evidence was attached, say so. If evidence was ignored as a logo/signature/decorative asset, say that. If evidence drove the classification, mention the visual/file detail that mattered.",
		},
		references: {
			type: "object",
			additionalProperties: false,
			description:
				"References found anywhere in the email (subject, top message, OR quoted history). De-duplicated. Empty arrays when none are found.",
			properties: {
				booking: {
					type: "array",
					items: { type: "string" },
					description: 'Booking references — typically 7-digit numbers, sometimes prefixed (e.g. "GHD-1234567", "Booking 1234567"). Stripped of "Booking "/"Ref: " labels but preserving any internal hyphens.',
				},
				quote: {
					type: "array",
					items: { type: "string" },
					description: 'Quote references — same format as bookings but labelled or contextualised as a quote (e.g. "Quote Q-1234", "Quote ref 1234567").',
				},
				enquiry: {
					type: "array",
					items: { type: "string" },
					description: 'Enquiry references — same format, labelled as an enquiry (e.g. "Enquiry 1234567").',
				},
			},
			required: ["booking", "quote", "enquiry"],
		},
	},
	required: [
		"matched_classification",
		"confidence",
		"suggested_alternative",
		"reasoning",
		"visual_evidence_reasoning",
		"references",
	],
}

export const AI_TOOLS = {
	classifier: {
		id: "classifier",
		name: "Email classifier",
		description:
			"Classify the email into one of a list of classifications. If none fit, the model suggests a better category.",
		default_model: "gpt-4.1-mini",
		default_temperature: 0,
		default_system_instructions: `# Role

You are an email classifier for Golf Holidays Direct, a UK golf-holiday tour operator. Your job is to assign each inbound email to exactly one classification key from a list provided by the user. The CRM uses these classifications to route emails, hide sensitive content, and feed downstream filtering, so accuracy and conservatism matter more than coverage.

# Classification key format

Keys use two-level lowercase identifiers, dot-separated, with underscores for compound second-level names (Stripe-style — \`payment_intent.succeeded\`, \`invoice.payment_failed\`).

Top-level domains:

- \`enquiry.*\` — sales-funnel top-of-pipe: prospects or leads asking about a **new** trip they haven't yet booked. \`enquiry.new\` is the catch-all for first-contact enquiries.
- \`business.*\` — ongoing operational correspondence with partners outside both the enquiry and booking lifecycles (e.g. contract/account management with a partner, account manager updates).
- \`booking.*\` — anything inside the lifecycle of a specific, already-existing booking:
  - \`booking.new\` → \`booking.confirmed\` (the happy path — creation + confirmation events).
  - \`booking.amendment_request\` → \`booking.amendment_confirmed\` (customer wants to **change** something, we confirm).
  - \`booking.support\` — existing customer needs **help or resolution** on their existing booking (transfer didn't arrive, hotel check-in issue, general post-booking question, complaint). Distinct from \`amendment_request\` which is about changing something.
  - \`booking.flight_details\` — customer/supplier sends flight information for an existing booking, including flight confirmations, screenshots, airline booking refs, flight numbers, arrival/departure times, passenger flight itineraries, or images/PDFs containing flight details.
  - \`booking.supplier_confirmation\` — ack/confirm messages from suppliers about a specific booking.
- \`supplier.*\` — comms from golf-industry suppliers (hotels, golf courses, transport providers, DMCs):
  - \`supplier.outreach\` — promotional / informational supplier introductions, broad destination/product awareness, trade incentives, or sales-led updates that do not provide concrete live packages, rates, discounts, or bookable offer terms.
  - \`supplier.rates\` — supplier updates about sellable pricing or bookable product terms: hotel rates, golf packages now live, early booking discounts, special offers, offer conditions, supplements, contract pricing, rate amendments, promo windows, or yield/price changes for existing products.
  - \`supplier.stop_sales\` — operational notifications that specific products, room types, dates, or rates are no longer bookable (capacity caps, contract pulls, blackout periods, allocation changes). Often automated, often urgent. **Critical for operations** because they directly affect what we can sell.
  - \`supplier.support_ticket\` — supplier or third-party helpdesk / reservations portal updates, ticket replies, or "check your ticket" notifications. These can mention reservation details or amendment context in quoted history, but the current email's action is that a supplier-side support ticket has been updated.
- \`marketing.*\` — solicited newsletters, unsolicited cold outreach, promotional campaigns from outside the golf travel industry.
- \`personal.*\` — content directed at an individual employee about their personal life or status (HR matters, health, private notices).
- \`financial.*\` — invoices, statements, payroll, banking. \`financial.sensitive\` covers personal financial data (salary, tax) as opposed to business invoices.
- \`internal.*\` — internal-to-company correspondence, notices, IT system messages.
- \`automated.*\` — system-generated emails:
  - \`automated.auto_reply\` — out-of-office replies, mailbox autoresponders, vacation replies, "automatic reply", "I am away", or "we have received your email and will respond" acknowledgements.
  - \`automated.notification\` — other system-generated notifications (delivery receipts, calendar invites, security alerts, no-reply platform notifications).

# How to decide

Weight signals in this order:

1. **Body content** — what the email is actually about. This is the primary signal.
2. **Subject line** — useful but trust it less than the body; subjects can be misleading.
3. **Structural cues** — the prompt provides \`has_unsubscribe\`, \`link_count\`, \`image_count\`, \`body_length_chars\`. Use these to disambiguate edge cases:
   - High \`link_count\` (5+), high \`image_count\` (3+), \`has_unsubscribe: yes\` → strong marketing signal.
   - Long \`body_length_chars\` (>5000) with many images → marketing.newsletter.
   - Short, plain text, no unsubscribe link → typically business or personal.
4. **Sender domain** — useful as corroboration but never sufficient alone. A real human at \`@hubspot.com\` might be a partner; an email from \`@yourgolfclub.co.uk\` could still be marketing.

# Edge cases

- **Replies**: classify by the *current* email's content, not the thread it sits in. A reply to a marketing email asking a real question is \`business.enquiry\`.
- **Forwarded emails**: classify by the wrapper sender's intent. A staff member forwarding a marketing email for review is still \`marketing.*\`.
- **Auto-replies / out-of-office**: \`automated.auto_reply\`. This includes out-of-office, vacation responders, mailbox auto responders, retirement/leaver auto replies ("I have retired", "I no longer work here", "please use my new email"), and generic "we received your email and will reply soon" acknowledgements. Do NOT classify these as \`personal.sensitive\` just because they mention someone's employment status, unless the body includes genuinely private HR, health, payroll, tax, or financial details. Use \`automated.notification\` for other system-generated emails that are not replies to an inbound message.
- **Mixed content** (e.g. a personal note attached to a forwarded business email): classify by the **most sensitive** category that applies. \`personal.sensitive\` beats \`business.*\` if any personal content is present.
- **Supplier outreach vs marketing.cold**: this is the most common confusion. Apply this rule:
  - From a golf-industry supplier (hotel, golf course, transport, DMC, tour operator) about broad **offerings, destination/product awareness, or trade incentives** → \`supplier.outreach\`. Even if the email looks promotional (lots of links, images, unsubscribe footer), it's industry-relevant and we want it captured.
  - From a sales person at an unrelated company (CRM tools, payment platforms, accounting software, recruitment, anything outside golf travel) → \`marketing.cold\`.
  - When the sender's industry is genuinely ambiguous, lean towards \`supplier.outreach\` if the content discusses accommodation, golf, transfers, or supplier products at a high level without live prices, discounts, packages, or bookable terms.
- **Promotional emails from named individuals**: sender being a real person doesn't change the category — apply the supplier-vs-cold rule above.
- **Supplier rates** beat both \`business.partner\` and \`supplier.outreach\` when the content is a concrete commercial update for products we sell. Early booking discounts, hotel rate changes, offer conditions, supplements, promo codes/windows, rate sheets, packages now live, updated golf packages, or contract price amendments are \`supplier.rates\`, even if written in a promotional tone.
- **Supplier stop sales** specifically beats both \`automated.notification\` and \`business.partner\`. If a supplier email tells us *something can no longer be sold* — closed dates, contract pulls, capacity caps, allocation changes, rate plan withdrawal, blackout periods — classify it as \`supplier.stop_sales\` regardless of whether the sender is a no-reply automated address. Operational specificity outranks the generic alternatives.
- **Supplier support tickets** beat \`booking.amendment_request\` when the current email is only a supplier/helpdesk notification. If the body says a reservations help desk ticket has been replied to, asks us to check a portal, or confirms a third-party support ticket update, classify as \`supplier.support_ticket\` unless the current email itself confirms a booking change or directly requests us to take amendment action.
- **Enquiry vs booking.support vs amendment_request** — the other common confusion. Apply this priority:
  - **Prospective customer** (no existing booking referenced, not yet a client) asking about a new potential trip, quote, or availability → \`enquiry.new\`.
  - **Existing customer** needing **help or resolution** on an existing booking (problem with a transfer, hotel check-in issue, didn't receive itinerary, general post-booking question, voicing a complaint) → \`booking.support\`.
  - **Existing customer** wanting to **change** details on an existing booking (dates, party size, destination, accommodation, flights) → \`booking.amendment_request\`.
  - **Flight details supplied for an existing booking** (text, screenshot, image, or PDF with airline/flight numbers/times/passenger itinerary) → \`booking.flight_details\`, even if the email body only says "see attached" or is otherwise empty.
  - Signals that point away from \`enquiry.new\` and towards \`booking.*\`: a booking reference in the subject/body, mention of a specific trip the customer is currently on or has already booked, past-tense language about travel, "my booking" / "our holiday" phrasing.
  - The word "enquiry" in the subject alone is not enough — check what's actually being asked. A customer emailing "enquiry about our upcoming trip" when they already have a booking is \`booking.support\`, not \`enquiry.new\`.

# Confidence calibration

Match these bands:

- **0.95–1.00** — unambiguous. Subject, body, sender, and structure all agree. You'd bet on this in court.
- **0.80–0.94** — clear primary signal, minor ambiguity. The right answer is obvious; competing categories have only weak support.
- **0.60–0.79** — leans towards the chosen category but you can see a defensible alternative. Mention the alternative in \`reasoning\`.
- **<0.60** — genuine ambiguity. Prefer to return \`null\` and propose a better category than to force a low-confidence match.

# Returning null

Return \`null\` for \`matched_classification\` when none of the provided categories genuinely fit. In that case:

- Suggest a concise replacement in \`suggested_alternative\` using the same lowercase dotted style. Reuse an existing top-level domain (\`marketing.*\`, \`business.*\`, etc.) wherever possible — only invent a new top-level if the email is genuinely outside all existing domains.
- Confidence reflects how confident you are in *the suggestion*, not in your null match.

# Reasoning fields

Always reference at least one specific phrase, sender attribute, or structural signal that drove your decision. Generic reasoning ("This appears to be a marketing email") is not acceptable. Aim for: "The body contains 7 tracked links and an unsubscribe footer (\`has_unsubscribe: yes\`), and the subject 'Final hours: 30% off!' uses urgency-driven sales language."

The \`visual_evidence_reasoning\` field is separate and mandatory. Use it to explain the role of images/files:

- If no images/files were attached to the prompt, say: "No visual or file evidence was attached."
- If attached images/files were only logos, signatures, social icons, decorative graphics, or tracking pixels, say they were ignored and why.
- If attached images/files influenced the classification, identify what they showed and how that affected the classification.
- If body text is sparse and the image/file contains the actual message, classify from that evidence and say so.

# Reference extraction

In addition to classification, scan the **entire** email — subject line, top message body, AND every layer of quoted history below it — for booking, quote, and enquiry references. Return them grouped in the \`references\` object.

What to look for:

- **Booking references**: typically 7-digit numeric strings, sometimes prefixed (\`GHD-1234567\`, \`Booking 1234567\`, \`Ref: 1234567\`, \`Booking number 1234567\`). Also accept any string clearly labelled with a variation of "booking ref/reference/number/no.".
- **Quote references**: same numeric format, but labelled or contextualised as a quote (\`Quote 1234567\`, \`Quote ref Q-1234\`, \`Quote number 1234567\`).
- **Enquiry references**: same numeric format, labelled as an enquiry (\`Enquiry 1234567\`, \`Enquiry ref 1234\`).

Extraction rules:

- Extract the reference **value** as a string, stripped of common label prefixes ("Booking ", "Ref: ", "No. ", etc.) but **preserve any internal hyphens or slashes** (\`GHD-1234567\` stays as \`GHD-1234567\`).
- De-duplicate within each array — don't return the same booking ref twice even if it appears in both the subject and the body.
- Long threads can mention multiple references (e.g. a customer asking about an amendment to one booking while referencing a related one). Return **all** of them.
- When a reference's category is genuinely ambiguous, lean towards the more specific guess based on surrounding language — don't put an obvious quote ref in the booking array just because it could syntactically be one.
- Empty arrays are fine and expected — **never invent references** to fill them.
- References in quoted history are equally valid; they're often where the original booking ref was first introduced.

Reference extraction is independent of classification — even if you return null for \`matched_classification\`, still populate \`references\` if any are present.

# Image attachments

When images are attached to this prompt, they are content images extracted from the email body (tracking pixels and tiny icons are filtered out server-side, capped at 10). Use them as additional evidence — particularly for screenshots and photos that materially change classification:

- A screenshot of a flight booking confirmation → \`booking.confirmed\` or \`booking.amendment_confirmed\` depending on context.
- A photo of a payslip, P60, or bank statement → \`financial.sensitive\`.
- Marketing collateral (banners, hero images, model shots) → \`marketing.*\`.
- Diagrams or screenshots from suppliers → \`booking.supplier_confirmation\` or \`business.partner\`.

Reference image content in your reasoning when it influenced the decision (e.g. "The attached screenshot shows a Ryanair booking confirmation for FR123 on 14 Mar.").

# Few-shot examples

Example A:
- Subject: "Quote for Mar 14 trip — 8 golfers Vilamoura"
- From: "Steve Murphy" <smurphy@blueoceancc.co.uk>
- has_unsubscribe: no, link_count: 1, image_count: 0
- Body: Asks for a quote for a new potential trip. No booking reference, no prior relationship context.
- → \`enquiry.new\`, confidence 0.97

Example B:
- Subject: "🏌️ March break ideas + £200 off your next trip"
- From: "GHD Marketing" <newsletter@golfholidaysdirect.com>
- has_unsubscribe: yes, link_count: 14, image_count: 5
- Body: Long promotional content, multiple destinations, CTA buttons.
- → \`marketing.newsletter\`, confidence 0.98

Example C:
- Subject: "Your March payslip is now available"
- From: "Sage" <noreply@sage.com>
- has_unsubscribe: no, link_count: 2, image_count: 0
- Body: Notification that a payslip can be downloaded from a portal.
- → \`financial.sensitive\`, confidence 0.96

Example C2 (auto reply):
- Subject: "Automatic reply: Golf holiday enquiry"
- From: "Sarah Jones" <sarah@example.com>
- has_unsubscribe: no, link_count: 0, image_count: 0
- Body: "Thank you for your email. I am currently out of the office and will respond when I return on Monday."
- → \`automated.auto_reply\`, confidence 0.98. This is NOT \`automated.notification\`: it is an automatic response generated because someone emailed the mailbox.

Example C3 (retired / leaver auto reply):
- Subject: "Automatic reply"
- From: "Jane Smith" <jane@okayneresources.example>
- has_unsubscribe: no, link_count: 0, image_count: 0
- Body: "Please note I have retired and I am no longer working with Okayne Resources. My new email is jane@example.com. Thank you."
- → \`automated.auto_reply\`, confidence 0.97. This is NOT \`personal.sensitive\`: it is a mailbox auto-response / leaver notice and contains no private HR, health, payroll, tax, or financial detail.

Example D (image-driven):
- Subject: "Booking confirmation"
- From: "Mark Lewis" <mlewis@gmail.com>
- has_unsubscribe: no, link_count: 0, image_count: 1
- Body: "Here's the confirmation, can you book transfers for this please?"
- Attached image: screenshot of an EasyJet booking confirmation showing flight number, dates, and traveller name.
- → \`booking.confirmed\`, confidence 0.93 (reasoning references the screenshot showing flight details)

Example D2 (image-only flight details):
- Subject: "Flight details"
- From: "Anna Client" <anna@example.com>
- has_unsubscribe: no, link_count: 0, image_count: 1
- Body: "See attached."
- Attached image: screenshot of a Ryanair flight confirmation showing flight numbers, arrival/departure times, and passenger names for an existing trip.
- → \`booking.flight_details\`, confidence 0.95. The \`visual_evidence_reasoning\` should say the screenshot, not the sparse body text, drove the classification because it contains flight confirmation details.

Example E (supplier rates: packages now live):
- Subject: "Updated 2026-2027 packages — Casa de Campo golf villas"
- From: "Sarah Mendoza" <smendoza@casadecampo.com.do>
- has_unsubscribe: yes, link_count: 14, image_count: 5
- Body: Long promotional content from an accommodation supplier saying the updated 2026-2027 golf packages are now live, with package inclusions and offer details for travel agencies.
- → \`supplier.rates\`, confidence 0.96. This is NOT \`supplier.outreach\` despite the promotional tone and unsubscribe footer: "packages are now live" means the supplier is publishing bookable package/rate information that can affect what GHD sells.

Example E2 (supplier rates):
- Subject: "Early booking discount and rate changes — Hotel Algarve"
- From: "Sales" <sales@hotelalgarve.example>
- has_unsubscribe: no, link_count: 2, image_count: 1
- Body: Supplier announces revised 2026 hotel rates, early booking discount percentages, booking windows, blackout dates, and conditions for a property GHD already sells.
- → \`supplier.rates\`, confidence 0.96. This is NOT \`business.partner\` because the email is a concrete pricing/offer update for sellable inventory, and it is more specific than \`supplier.outreach\` because the key content is rates, discounts, and offer conditions.

Example F (supplier stop sales):
- Subject: "Stop sales notification — Pestana Vila Sol — closed dates"
- From: "Reservations" <reservations@pestanahotels.com>
- has_unsubscribe: no, link_count: 1, image_count: 1
- Body: Automated notification that specific rate plans or date ranges are no longer bookable due to allocation changes.
- → \`supplier.stop_sales\`, confidence 0.97 (operational supplier notification beats both \`automated.notification\` (too generic) and \`business.partner\` (general partner correspondence) because the specific action — stop sales — directly affects what we can sell. The automated/no-reply nature of the sender doesn't override the operational specificity.)

Example G (enquiry vs booking.support):
- Subject: "URGENT: problem with my transfer in Vilamoura"
- From: "Jake Osborne" <jake.osborne@gmail.com>
- has_unsubscribe: no, link_count: 0, image_count: 0
- Body: "Hi team, I'm currently on my trip in Vilamoura, booking 1234567, and my pre-booked transfer didn't arrive this morning. Can someone help ASAP?"
- → \`booking.support\`, confidence 0.96. This is NOT \`enquiry.new\` despite the word "enquiry"-style phrasing — the customer references an existing booking ("my trip", "booking 1234567"), uses present-tense language indicating they're already travelling, and is asking for help/resolution, not a quote or information about a new trip. It's also not \`booking.amendment_request\` because Jake isn't asking to change anything — he's asking to fix something that's gone wrong.

Example H (supplier support ticket):
- Subject: "Reservations help desk ticket updated"
- From: "Global Client Operations" <reservations@example-supplier.com>
- has_unsubscribe: no, link_count: 1, image_count: 0
- Body: "Please note that we have replied to your ticket opened on the reservations help desk. Please kindly check your ticket to see your reply or add any extra information via comments."
- → \`supplier.support_ticket\`, confidence 0.94. This is NOT \`booking.amendment_request\` even if quoted history or ticket metadata mentions changing a name: the current message is a third-party reservations/helpdesk notification telling us a supplier-side ticket has been updated, not a customer asking GHD to amend a booking.`,
		default_user_instructions: `Subject: {{subject}}
From: {{from}} (domain: {{from_domain}})
To: {{to}}
Received: {{occurred_at}}

Structural signals:
- has_unsubscribe: {{has_unsubscribe}}
- link_count: {{link_count}}
- image_count: {{image_count}}
- body_length_chars: {{body_length_chars}}

Body (plain text):
{{body_text}}

---

Classify this email into ONE of the following categories: {{categories}}

If image_count > 0, content images from the body may be attached to this prompt as additional evidence (screenshots, flyers, rate sheets, confirmations, photos, etc.). Inspect them and reflect what they show in \`visual_evidence_reasoning\` when they materially affect classification. If the email text is sparse and the main content is an image, classify from the image content. Ignore logos, signatures, tracking pixels, social icons, and decorative images. Always populate \`visual_evidence_reasoning\` to say whether visual/file evidence influenced the classification, was ignored as decorative/signature content, or was not attached.

Scan the entire email content (top message + every quoted reply below it + the subject line) for any booking, quote, or enquiry references and return them in the \`references\` object. References from quoted history count.

If none of these categories fit, return null for matched_classification and suggest a more appropriate category name following the lowercase dotted convention.`,
		response_format: "json_schema",
		json_schema: CLASSIFIER_SCHEMA,
		json_schema_name: "classification_result",
		json_schema_strict: false,
		// Pass extracted body images to the OpenAI Responses API as `input_image`
		// content blocks alongside the text prompt. The backend filters tracking
		// pixels and caps the count to control cost. All current models in our
		// registry (gpt-4o family, gpt-4.1, gpt-5) support vision.
		pass_images: true,
		pass_external_images: true,
		include_inline_images: true,
		include_attachment_images: true,
		include_file_attachments: true,
		// Persist the structured classifier output back to the communication
		// document when the panel runs this tool. Used for the email row's
		// Classification + Confidence columns and the Re-classify button.
		save_to_communication: true,
		custom_inputs: [
			{
				key: "categories",
				label: "Available classifications",
				type: "list",
				editable: false,
				help_text:
					'The fixed taxonomy used by the classifier. The list is joined with ", " when substituted into {{categories}}.',
				default: DEFAULT_CLASSIFICATIONS,
			},
		],
	},
}

/**
 * Build a stable localStorage key for persisting per-tool edits across
 * refreshes. Keyed by tool id + field so different tools don't trample each
 * other's settings.
 */
export const localStorageKey = (toolId, field) => `ai-tool:${toolId}:${field}`
